// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 5 — Your clock.
//
// The question: which hours of which months are the bad ones here?
//
// An hour-of-day × month calendar reads a daily cycle and a seasonal cycle at once, which is
// what this data actually has and what a line chart reads neither of. Two exclusions are drawn
// as visible hatched gaps rather than as low values, because both would otherwise manufacture a
// finding: Queensland runs automated zero and span checks between midnight and 1am so no ambient
// value can be reported then, and NSW invalidates an hour overnight on a rolling schedule.
// "The cleanest hour of the day is 1am" is an artefact waiting to happen.

import type { Bundle } from '../data';
import { loadClock } from '../data';
import type { Clock, ClockCell } from '../types';
import { el, emptyState, legend, panel, segmented, xScroll } from '../components/ui';
import { concentration, hourRange, MONTH_NAMES, number, pollutantLabel, prettyDate, tip, UNIT } from '../format';
import { bandColour, bandLabel, BANDS } from '../palette';
import { glossaryLink } from '../glossary';
import { getState, setState } from '../state';
import { openStation } from './stationDetail';

type Stat = 'median' | 'p95';

function drawCalendar(
  container: HTMLElement,
  cells: ClockCell[],
  stat: Stat,
  pollutant: 'pm2.5' | 'pm10',
  stationName: string,
  jurisdiction: string
): void {
  const cellW = 26;
  const cellH = 15;
  const left = 42;
  const top = 18;
  const width = left + 12 * cellW + 8;
  const height = top + 24 * cellH + 6;

  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  node.setAttribute('viewBox', `0 0 ${width} ${height}`);
  node.setAttribute('class', 'chart');
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', `${pollutantLabel(pollutant)} by hour of day and month at ${stationName}`);
  node.style.width = '100%';
  node.style.maxWidth = `${width * 1.6}px`;
  node.style.height = 'auto';

  const make = (tag: string, attrs: Record<string, string | number>) => {
    const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, String(value));
    return element;
  };

  MONTH_NAMES.forEach((month, index) => {
    const text = make('text', { x: left + index * cellW + cellW / 2, y: 12, fill: 'var(--text-tertiary)', 'font-size': 8, 'text-anchor': 'middle' });
    text.textContent = month[0];
    node.append(text);
  });

  for (let hour = 0; hour < 24; hour++) {
    if (hour % 3 === 0) {
      const text = make('text', { x: 36, y: top + hour * cellH + 11, fill: 'var(--text-tertiary)', 'font-size': 8, 'text-anchor': 'end' });
      text.textContent = hour === 0 ? '12am' : hour === 12 ? '12pm' : hour < 12 ? `${hour}am` : `${hour - 12}pm`;
      node.append(text);
    }
    for (let month = 0; month < 12; month++) {
      const cell = cells[hour * 12 + month];
      const value = cell ? (stat === 'median' ? cell[0] : cell[1]) : null;
      const rect = make('rect', {
        x: left + month * cellW,
        y: top + hour * cellH,
        width: cellW - 1,
        height: cellH - 1,
        rx: 2,
        fill: value === null ? 'var(--missing)' : bandColour(value, pollutant),
        'fill-opacity': value === null ? 0.3 : 1,
      });
      // A missing bin is hatched, never faded toward the ground — on this site fading toward the
      // ground means clean air, and an instrumented hour is not clean air.
      if (value === null) rect.setAttribute('stroke-dasharray', '2 2');
      rect.style.cursor = 'pointer';
      rect.setAttribute(
        'data-tip',
        cell
          ? tip(
              `${stationName} (${jurisdiction})`,
              `${hourRange(hour)}, ${MONTH_NAMES[month]}`,
              `Median ${concentration(cell[0])} ${UNIT} · 95th percentile ${concentration(cell[1])} ${UNIT}`,
              `${bandLabel(value, pollutant)} on the ${stat === 'median' ? 'median' : '95th percentile'}`,
              `Worst ever in this bin: ${concentration(cell[3])} ${UNIT} on ${prettyDate(cell[4])}`,
              `${number(cell[2])} hours measured`
            )
          : tip(
              `${hourRange(hour)}, ${MONTH_NAMES[month]}`,
              'Too few measured hours in this bin to summarise (under 20).',
              hour === 0
                ? 'Queensland runs automated instrument checks between midnight and 1am, so no ambient value can be reported for that hour.'
                : 'Instruments are taken out of service for calibration and repair.'
            )
      );
      node.append(rect);
    }
  }
  container.append(xScroll(node));
}

export async function renderClock({ bundle, container }: { bundle: Bundle; container: HTMLElement }): Promise<void> {
  const state = getState();
  const station = bundle.stations.find((candidate) => candidate.id === state.station) ?? bundle.stations[0];

  const view = panel({
    title: 'Your clock',
    subtitle: `Every hour ever recorded at ${station.name}, arranged by hour of the day and month of the year. Twenty-four rows, twelve columns, and every cell the summary of hundreds of hours.`,
  });
  container.append(view.root);

  let stat: Stat = 'median';
  const controls = el('div', 'control-row');
  controls.append(
    segmented(
      [
        { value: 'median', label: 'Typical hour', tip: 'The median of every hour that ever fell in this bin' },
        { value: 'p95', label: 'Bad hour', tip: 'The 95th percentile — one hour in twenty is worse than this' },
      ],
      stat,
      (value) => {
        stat = value as Stat;
        void draw();
      }
    )
  );

  const openButton = el('button', 'button');
  openButton.type = 'button';
  openButton.textContent = `Open ${station.name}`;
  openButton.addEventListener('click', () => void openStation(station));
  controls.append(openButton);
  view.body.append(controls);

  const host = el('div', 'clock-panels');
  view.body.append(host);

  let clock: Clock | null = null;

  async function draw(): Promise<void> {
    if (!clock) clock = await loadClock(station.id);
    host.innerHTML = '';
    if (!clock || !Object.keys(clock).length) {
      host.append(
        emptyState(
          `No hourly calendar is published for ${station.name}.`,
          'A station needs at least twenty measured hours in a bin before this site will summarise it.'
        )
      );
      return;
    }
    for (const pollutant of ['pm2.5', 'pm10'] as const) {
      const cells = clock[pollutant];
      if (!cells) continue;
      const column = el('div', 'split-col');
      column.append(el('h3', 'sub-head', pollutantLabel(pollutant)));
      column.append(
        el(
          'p',
          'lorenz-caption',
          pollutant === 'pm2.5'
            ? 'Combustion particles — smoke, wood heaters, engines.'
            : 'Everything coarser as well — dust, soil, sea salt, brake and tyre wear.'
        )
      );
      drawCalendar(column, cells, stat, pollutant, station.name, station.jurisdiction);
      host.append(column);
    }

    // The reading, said in words. A heat calendar is useless to someone who has not read one
    // before unless the site tells them what it found.
    const pm = clock['pm2.5'];
    if (pm) {
      let worstBin: { hour: number; month: number; value: number } | null = null;
      for (let hour = 0; hour < 24; hour++) {
        for (let month = 0; month < 12; month++) {
          const cell = pm[hour * 12 + month];
          if (!cell) continue;
          const value = stat === 'median' ? cell[0] : cell[1];
          if (!worstBin || value > worstBin.value) worstBin = { hour, month, value };
        }
      }
      if (worstBin) {
        view.body.append(
          el(
            'p',
            'note',
            `At ${station.name} the worst ${stat === 'median' ? 'typical' : '95th-percentile'} hour of the year is <strong>${hourRange(
              worstBin.hour
            )} in ${MONTH_NAMES[worstBin.month]}</strong>, at ${concentration(worstBin.value)} ${UNIT}. ` +
              (worstBin.month >= 4 && worstBin.month <= 7 && (worstBin.hour >= 17 || worstBin.hour <= 8)
                ? `A winter evening or early morning peak is the signature of a ${glossaryLink(
                    'inversion',
                    'temperature inversion'
                  )} over domestic wood heating, not of traffic or industry.`
                : worstBin.hour >= 10 && worstBin.hour <= 16
                  ? 'A middle-of-the-day peak points at something photochemical or wind-blown rather than at local combustion.'
                  : 'A morning peak usually points at traffic, a shallow overnight boundary layer, or both at once.')
          )
        );
      }
    }
  }

  await draw();

  view.body.append(
    el(
      'p',
      'note',
      `Each cell summarises every hour ever measured in that hour-of-day and month bin, so a cell is hundreds of hours, not one. Bins with fewer than twenty measured hours are drawn hatched rather than filled — an instrument out for calibration is not clean air. ` +
        `Queensland's midnight hour is systematically instrumented rather than measured: automated zero and span checks run between midnight and 1am at Queensland Government sites and no ambient hourly value can be reported then, so "the cleanest hour of the day" is a conclusion this chart deliberately refuses to support. ` +
        `Colours follow the published one-hour category scale, and ${glossaryLink('pm2.5')} and ${glossaryLink(
          'pm10'
        )} are drawn on their own scales because their thresholds differ.`
    )
  );

  const next = el('div', 'control-row');
  const toRose = el('button', 'button');
  toRose.type = 'button';
  toRose.textContent = 'Is it made here or blown here?';
  toRose.addEventListener('click', () => setState({ view: 'rose' }));
  next.append(toRose);
  view.body.append(next);

  view.body.append(legend(BANDS.map((band) => ({ label: band.label, hex: band.hex }))));
}
